import { gql, useLazyQuery, useQuery } from '@apollo/client'

import type { CustomerAddress } from '@@types'

/**
 * GraphQL запрос для получения адресов авторизованного пользователя
 * Аналог useFetchMyAddresses из приложения
 */
export const GQL_MY_ADDRESSES = gql`
  query MyAddresses($showPickupShops: Boolean) {
    myAddresses(showPickupShops: $showPickupShops) {
      id
      label
      shop_id
      address
      address_appartments
      address_intercom
      address_porch
      address_floor
      door_delivery
      no_door_call
      customerGeoPoint {
        latitude
        longitude
      }
      other_recipient
      recipient_phone
      recipient_name
      recipient_last_name
      comment
      shop {
        id
        name
        address
        min_price
        minOrderCost
        open_time
        close_time
        shopGeoPoint {
          latitude
          longitude
        }
        firstAvailableSlot {
          period {
            from
            to
          }
          slotTime
          slotDate
        }
      }
    }
  }
`

export interface MyAddressesQuery {
  myAddresses?: CustomerAddress[]
}

interface MyAddressesVariables {
  showPickupShops?: boolean
}

interface UseFetchMyAddressesProps {
  showPickupShops?: boolean
  skip?: boolean
}

/**
 * Хук для получения адресов авторизованного пользователя
 * @param showPickupShops - показывать ли pickup точки
 * @param skip - пропустить запрос (например для неавторизованных)
 */
export function useFetchMyAddresses ({
  showPickupShops,
  skip = false,
}: UseFetchMyAddressesProps = {}) {
  const { data, loading, error, refetch } = useQuery<
    MyAddressesQuery,
    MyAddressesVariables
  >(GQL_MY_ADDRESSES, {
    variables: {
      showPickupShops,
    },
    skip,
  })

  const addresses = data?.myAddresses ?? []
  
  // Разделяем на delivery адреса и pickup точки
  const deliveryAddresses = addresses.filter((address) => !address.shop_id)
  const pickupShops = addresses.filter((address) => !!address.shop_id)

  return {
    data,
    addresses,
    deliveryAddresses,
    pickupShops,
    loading,
    error,
    refetch,
  }
}

/**
 * Lazy-хук для получения адресов авторизованного пользователя
 * Используется когда нужно запустить запрос программно
 */
export const useLazyFetchMyAddresses = () => 
  useLazyQuery<MyAddressesQuery, MyAddressesVariables>(GQL_MY_ADDRESSES)
